/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { createContext, useContext, useRef, useState } from 'react';

interface SalesDataContextType {
  cache: React.MutableRefObject<Map<string, any>>;
  selectedValue: number;
  setSelectedValue: (value: number) => void;
  expandedOrderTotals: string | null;
  setExpandedOrderTotals: (value: string | null) => void;
  expandedOrderItems: string | null;
  setExpandedOrderItems: (value: string | null) => void;
  hasUserMadeSelection: boolean;
  setHasUserMadeSelection: (value: boolean) => void;
}

const SalesDataContext = createContext<SalesDataContextType | null>(null);

export const SalesDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cache = useRef<Map<string, any>>(new Map());
  const [selectedValue, setSelectedValue] = useState<number>(0);
  const [expandedOrderTotals, setExpandedOrderTotals] = useState<string | null>(null);
  const [expandedOrderItems, setExpandedOrderItems] = useState<string | null>(null);
  const [hasUserMadeSelection, setHasUserMadeSelection] = useState<boolean>(false);

  return (
    <SalesDataContext.Provider
      value={{
        cache,
        selectedValue,
        setSelectedValue,
        expandedOrderTotals,
        setExpandedOrderTotals,
        expandedOrderItems,
        setExpandedOrderItems,
        hasUserMadeSelection,
        setHasUserMadeSelection,
      }}
    >
      {children}
    </SalesDataContext.Provider>
  );
};

export const useSalesData = () => {
  const context = useContext(SalesDataContext);
  if (!context) {
    throw new Error('useSalesData must be used within SalesDataProvider');
  }
  return context;
};